// The recording, on every page but Record (which says all of it): ONE
// notice at a time (barModel.recordingNotice), either the live bar
// ("Recording 12:34 · Weekly catch-up · Stop", or the upload still going)
// or the one-tap question after a recording stopped on its own ("Carry
// on" / "Finish now"). Stop asks first: one stray tap must not end a
// meeting's recording.
//
// Both are a strip in the page's own layout (RecordingSlot, mounted by the
// desktop and phone shells under their header): the page moves down for
// it, so it never covers what is on the page, and a dialog's backdrop
// covers it like the rest of the page. Only where no shell has a slot
// does it float at the top.
//
// RecordingSignedOutBar is the same bar behind the sign-in page: a
// recording carries on through a sign-out, and the person must see it.

import { useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, Mic, Square, X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cx } from "@/components/ui/controlStyles";
import { useAuth } from "@/stores/auth";
import { recordingNotice } from "./barModel";
import { barWords, offerLine } from "./copy";
import { useRecorder, type RecorderControls } from "./useRecorder";

export const clock = (s: number) => {
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const mm = String(m).padStart(2, "0"), ss = String(sec).padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
};

/** Stop, after one more tap: the first turns the button into "Stop and save" / "Keep recording" for a few seconds. */
export function useConfirmStop(stop: () => void) {
  const [asking, setAsking] = useState(false);
  useEffect(() => {
    if (!asking) return;
    const t = window.setTimeout(() => setAsking(false), 6_000);
    return () => window.clearTimeout(t);
  }, [asking]);
  return {
    asking,
    ask: () => setAsking(true),
    confirm: () => {
      setAsking(false);
      stop();
    },
    cancel: () => setAsking(false),
  };
}

/** Floating at the top: only where no shell has a slot (and behind the sign-in page). */
function Frame({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "live" }) {
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[115] flex justify-center px-3 pt-[calc(env(safe-area-inset-top)+8px)]">
      <div
        role="status"
        className={cx(
          "pointer-events-auto flex max-w-[480px] flex-wrap items-center gap-x-3 gap-y-1.5 rounded-[18px] bg-overlay px-3 py-2 text-sm text-fg shadow-card-panel ring-1",
          tone === "live" ? "ring-danger-fill/45" : "ring-line-subtle",
        )}
      >
        {children}
      </div>
    </div>
  );
}

/** The strip in the page's layout, under the shell's header. `stack` (the phone): its own gutter. */
function Strip({ children, tone, stack }: { children: React.ReactNode; tone: "neutral" | "live" | "ask"; stack: boolean }) {
  return (
    <div
      role="status"
      className={cx(
        "shrink-0 border-b py-2",
        stack ? "px-4" : "px-4 md:px-8",
        tone === "ask" ? "border-warning/40 bg-warning/10 py-2.5" : tone === "live" ? "border-danger-fill/35 bg-overlay" : "border-line-subtle bg-overlay",
      )}
    >
      {children}
    </div>
  );
}

const liveTone = (r: RecorderControls) => (r.phase === "recording" || r.phase === "stopping" ? "live" : "neutral");

/** The live bar's words and buttons: the same in the strip and floating. */
function LiveContent({ r, openTo }: { r: RecorderControls; openTo: string | null }) {
  const confirm = useConfirmStop(() => void r.stop());
  const recording = r.phase === "recording" || r.phase === "stopping";
  const words = barWords(r);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-fg">
      {/* The title under the timer on a narrow screen, beside it on a wide one: the buttons stay on the first line. */}
      <span className="flex min-w-0 flex-1 items-start gap-2 sm:items-center">
        {recording ? <span aria-hidden className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-danger-fill motion-safe:animate-pulse sm:mt-0" /> : <Mic aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-fg-muted sm:mt-0" />}
        <span className="flex min-w-0 flex-col sm:flex-row sm:items-baseline sm:gap-2">
          <span className="whitespace-nowrap font-medium tabular-nums">
            {r.phase === "stopping" ? "Saving…" : recording ? `Recording ${clock(r.elapsedSec)}` : r.unsent > 0 ? `Uploading · ${r.unsent}\u00a0to go` : "Finishing the upload"}
          </span>
          {r.title && <span className="truncate text-fg-muted sm:max-w-[320px]">{r.title}</span>}
        </span>
      </span>
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        {recording && !confirm.asking && (
          <Button size="sm" variant="danger" onClick={confirm.ask} disabled={r.phase === "stopping"}>
            <Square aria-hidden className="h-3 w-3" fill="currentColor" /> Stop
          </Button>
        )}
        {recording && confirm.asking && (
          <>
            <Button size="sm" variant="danger-solid" onClick={confirm.confirm}>Stop and save</Button>
            <Button size="sm" variant="secondary" onClick={confirm.cancel}>Keep recording</Button>
          </>
        )}
        {openTo && !confirm.asking && (
          <Link to={openTo} className="rounded-control px-2 py-1 text-xs font-medium text-accent-fg hover:underline">Open</Link>
        )}
      </span>
      {words && <span className="basis-full text-xs text-warning">{words}</span>}
    </div>
  );
}

function OfferButtons({ r, wide }: { r: RecorderControls; wide?: boolean }) {
  const held = r.offer!.finishAt !== null;
  return (
    <span className={cx("flex items-center gap-2", wide && "[&>*]:flex-1")}>
      {/* Straight from the tap: the microphone is asked for first. */}
      <Button size="sm" onClick={() => void r.carryOn()} loading={r.phase === "requesting"}>Carry on recording</Button>
      {held ? (
        <Button size="sm" variant="secondary" onClick={r.finishNow} disabled={r.phase === "requesting"}>Finish now</Button>
      ) : wide ? (
        <Button size="sm" variant="ghost" onClick={r.dismissOffer} disabled={r.phase === "requesting"}>Not now</Button>
      ) : (
        <Button size="icon-sm" variant="ghost" aria-label="Dismiss" onClick={r.dismissOffer} disabled={r.phase === "requesting"}><X aria-hidden className="h-4 w-4" /></Button>
      )}
    </span>
  );
}

/** The question's sentence, and why the cut take's parts still wait (when they do). */
function OfferWords({ r }: { r: RecorderControls }) {
  const words = barWords(r);
  return (
    <span className="flex min-w-0 flex-col gap-1">
      <span>{offerLine(r.offer!, r.storage)}</span>
      {words && <span className="text-xs text-warning">{words}</span>}
    </span>
  );
}

// How many shells have a slot on screen (the bar then floats nowhere).
let slots = 0;
const slotListeners = new Set<() => void>();
const slotSubscribe = (fn: () => void) => {
  slotListeners.add(fn);
  return () => slotListeners.delete(fn);
};
const slotCount = () => slots;

const openPath = (pathname: string) => (pathname.startsWith("/companion") ? "/companion/record" : "/record");

/**
 * The recording's one notice as a strip in the page's layout, under the
 * shell's header: the page moves down for it. `stack` (the phone) puts the
 * question's buttons under the sentence, side by side across the width.
 */
export function RecordingSlot({ stack = false }: { stack?: boolean }) {
  const r = useRecorder();
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    slots += 1;
    slotListeners.forEach((fn) => fn());
    return () => {
      slots -= 1;
      slotListeners.forEach((fn) => fn());
    };
  }, []);
  const notice = recordingNotice(r, pathname);
  if (notice === "live") {
    return (
      <Strip tone={liveTone(r)} stack={stack}>
        <LiveContent r={r} openTo={openPath(pathname)} />
      </Strip>
    );
  }
  if (notice !== "offer") return null;
  return (
    <Strip tone="ask" stack={stack}>
      <div className={cx("flex gap-x-4 gap-y-2.5", stack ? "flex-col" : "flex-col sm:flex-row sm:items-center")}>
        <p className="flex min-w-0 flex-1 items-start gap-2 text-sm text-fg">
          <AlertTriangle aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-warning" strokeWidth={1.75} />
          <OfferWords r={r} />
        </p>
        <OfferButtons r={r} wide={stack} />
      </div>
    </Strip>
  );
}

/** Floating, only where no shell has a slot on screen. */
export function RecordingBar() {
  const r = useRecorder();
  const { pathname } = useLocation();
  const slotted = useSyncExternalStore(slotSubscribe, slotCount, slotCount) > 0;
  if (slotted) return null;
  const notice = recordingNotice(r, pathname);
  if (notice === "live") {
    return (
      <Frame tone={liveTone(r)}>
        <LiveContent r={r} openTo={openPath(pathname)} />
      </Frame>
    );
  }
  if (notice !== "offer") return null;
  // The sentence over the buttons, never a narrow column beside them.
  return (
    <Frame>
      <span className="basis-full"><OfferWords r={r} /></span>
      <OfferButtons r={r} />
    </Frame>
  );
}

/** Behind the sign-in page (or a crashed app: RootBoundary): the recording goes on, and says so. */
export function RecordingSignedOutBar() {
  const r = useRecorder();
  const signedIn = useAuth((s) => s.authenticated);
  if (r.bound || !(r.phase === "recording" || r.phase === "stopping" || r.phase === "uploading")) return null;
  return (
    <Frame tone={liveTone(r)}>
      <LiveContent r={{ ...r, waiting: r.waiting === "none" && !signedIn ? "signed_out" : r.waiting }} openTo={null} />
    </Frame>
  );
}
